import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DataTable } from '@/components/DataTable';
import { ppmCoreApi } from '@/lib/api';
import { 
  Calendar, Clock, AlertTriangle, CheckCircle2, UserPlus, 
  Search, RefreshCw, Wrench, Building2, FileText, Download,
  Play, Pause, XCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

// Wyldecrest Stabilised Workflow - 8 Stages Only
const STATUS_COLORS = {
  'Job Created': 'bg-blue-100 text-blue-700',
  'Awaiting Quote': 'bg-purple-100 text-purple-700',
  'Quote Awaiting Approval': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Assigned': 'bg-cyan-100 text-cyan-700',
  'Scheduled': 'bg-teal-100 text-teal-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-slate-100 text-slate-600',
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

const JOB_TYPE_COLORS = {
  'PPM': 'bg-green-100 text-green-700',
  'Quoted Works': 'bg-purple-100 text-purple-700',
  'Reactive': 'bg-orange-100 text-orange-700',
  'Inspection': 'bg-blue-100 text-blue-700',
  'Variation': 'bg-amber-100 text-amber-700',
};

export default function PPMJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Form data
  const [assignData, setAssignData] = useState({ contractor_id: '', scheduled_date: '' });
  const [completeData, setCompleteData] = useState({ completion_notes: '', actual_amount: '', certificate_uploaded: false });
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [jobsRes, contractorsRes, dashRes] = await Promise.all([
        ppmCoreApi.getJobs({ limit: 500 }),
        axios.get(`${API}/api/contractors`, { headers }),
        ppmCoreApi.getDashboard(),
      ]);
      setJobs(jobsRes.jobs || []);
      setContractors(contractorsRes.data || []);
      setDashboard(dashRes.dashboard || {});
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredJobs = jobs.filter(j => {
    const matchesStatus = statusFilter === 'all' || j.status === statusFilter;
    const matchesType = jobTypeFilter === 'all' || j.job_type === jobTypeFilter;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (j.task_name || '').toLowerCase().includes(searchLower) ||
      (j.park_name || '').toLowerCase().includes(searchLower) ||
      (j.contractor_name || '').toLowerCase().includes(searchLower);
    return matchesStatus && matchesType && matchesSearch;
  });

  const handleAssign = async () => {
    if (!assignData.contractor_id) {
      toast.error('Select a contractor');
      return;
    }
    try {
      await ppmCoreApi.assignContractor(selectedJob.id, assignData.contractor_id, assignData.scheduled_date || null);
      toast.success('Contractor assigned');
      setShowAssignDialog(false);
      setAssignData({ contractor_id: '', scheduled_date: '' });
      loadData();
    } catch (err) {
      toast.error('Failed to assign contractor');
    }
  };

  const handleComplete = async () => {
    try {
      await ppmCoreApi.completeJob(selectedJob.id, {
        completion_notes: completeData.completion_notes || null,
        actual_amount: completeData.actual_amount ? parseFloat(completeData.actual_amount) : null,
        certificate_uploaded: completeData.certificate_uploaded,
      });
      toast.success('Job completed & PPM schedule updated');
      setShowCompleteDialog(false);
      setCompleteData({ completion_notes: '', actual_amount: '', certificate_uploaded: false });
      loadData();
    } catch (err) {
      toast.error('Failed to complete job');
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      await ppmCoreApi.updateJobStatus(job.id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleExportSchedule = async () => {
    try {
      const res = await ppmCoreApi.exportSchedule({});
      const schedule = res.schedule || [];
      
      // Convert to CSV
      const headers = ['Job ID', 'Task', 'Park', 'Address', 'Unit', 'Priority', 'Due Date', 'Scheduled Date', 'Contractor', 'Email', 'Phone'];
      const rows = schedule.map(j => [
        j.id, j.task_name, j.park_name, j.address, j.pitch_or_unit,
        j.priority, j.due_date?.split('T')[0], j.scheduled_date?.split('T')[0],
        j.contractor_name, j.contractor_email, j.contractor_phone
      ]);
      
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contractor_schedule_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      
      toast.success(`Exported ${schedule.length} scheduled jobs`);
    } catch (err) {
      toast.error('Failed to export schedule');
    }
  };

  const columns = [
    {
      accessorKey: 'job_type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge className={JOB_TYPE_COLORS[row.original.job_type] || 'bg-gray-100'}>
          {row.original.job_type}
        </Badge>
      )
    },
    {
      accessorKey: 'task_name',
      header: 'Task / Location',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.task_name}</div>
          <div className="text-xs text-slate-500">
            {row.original.park_name} {row.original.pitch_or_unit && `• ${row.original.pitch_or_unit}`}
          </div>
        </div>
      )
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge className={PRIORITY_COLORS[row.original.priority] || 'bg-gray-100'}>
          {row.original.priority}
        </Badge>
      )
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.due_date ? format(parseISO(row.original.due_date), 'dd MMM yyyy') : '-'}
        </span>
      )
    },
    {
      accessorKey: 'contractor_name',
      header: 'Contractor',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.contractor_name || '-'}</span>
      )
    },
    {
      accessorKey: 'scheduled_date',
      header: 'Scheduled',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.scheduled_date ? format(parseISO(row.original.scheduled_date), 'dd MMM yyyy') : '-'}
        </span>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge className={STATUS_COLORS[row.original.status] || 'bg-gray-100'}>
          {row.original.status}
        </Badge>
      )
    },
  ];

  const getActions = (job) => {
    const actions = [];
    
    if (['Awaiting Assignment', 'Approved'].includes(job.status)) {
      actions.push({
        label: 'Assign Contractor',
        icon: UserPlus,
        onClick: () => { setSelectedJob(job); setShowAssignDialog(true); },
        className: 'text-[#1F7A78]'
      });
    }
    
    if (['Assigned', 'Scheduled', 'In Progress'].includes(job.status)) {
      actions.push({
        label: 'Mark Complete',
        icon: CheckCircle2,
        onClick: () => { setSelectedJob(job); setShowCompleteDialog(true); },
        className: 'text-green-600'
      });
    }
    
    if (['Scheduled', 'Assigned'].includes(job.status)) {
      actions.push({
        label: 'Start Work',
        icon: Play,
        onClick: () => handleStatusChange(job, 'In Progress'),
        className: 'text-blue-600'
      });
    }
    
    if (!['Completed', 'Closed', 'Cancelled'].includes(job.status)) {
      actions.push({
        label: 'Put On Hold',
        icon: Pause,
        onClick: () => handleStatusChange(job, 'On Hold'),
        className: 'text-yellow-600'
      });
      actions.push({
        label: 'Cancel',
        icon: XCircle,
        onClick: () => handleStatusChange(job, 'Cancelled'),
        className: 'text-red-600'
      });
    }
    
    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#1F7A78]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">PPM & Quoted Works Jobs</h1>
          <p className="text-slate-500">Execution layer for all PPM and quoted works</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExportSchedule}>
            <Download className="h-4 w-4 mr-2" />
            Export Schedule
          </Button>
        </div>
      </div>

      {/* Stats */}
      {dashboard?.jobs && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
          <Card className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('Awaiting Assignment')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.awaiting_assignment}</div>
              <div className="text-xs text-slate-500">Awaiting Assignment</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('Awaiting Quote')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.awaiting_quote}</div>
              <div className="text-xs text-slate-500">Awaiting Quote</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-indigo-500 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('Awaiting Approval')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.awaiting_approval}</div>
              <div className="text-xs text-slate-500">Awaiting Approval</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-[#1F7A78] cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('Approved')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.approved || 0}</div>
              <div className="text-xs text-slate-500">Approved</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('Scheduled')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.scheduled}</div>
              <div className="text-xs text-slate-500">Scheduled</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('In Progress')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.in_progress}</div>
              <div className="text-xs text-slate-500">In Progress</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500 cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.jobs.completed_this_week}</div>
              <div className="text-xs text-slate-500">Completed (Week)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Search jobs..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="jobs-search-input"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Awaiting Assignment">Awaiting Assignment</SelectItem>
            <SelectItem value="Awaiting Quote">Awaiting Quote</SelectItem>
            <SelectItem value="Awaiting Approval">Awaiting Approval</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Assigned">Assigned</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="In Progress">In Progress</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
          <SelectTrigger className="w-[150px]" data-testid="type-filter">
            <SelectValue placeholder="Job Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="PPM">PPM</SelectItem>
            <SelectItem value="Quoted Works">Quoted Works</SelectItem>
            <SelectItem value="Reactive">Reactive</SelectItem>
            <SelectItem value="Inspection">Inspection</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable 
            columns={columns} 
            data={filteredJobs}
            actions={getActions}
            isLoading={loading}
            emptyMessage="No jobs found. Run the PPM scheduler to create jobs from due assignments."
          />
        </CardContent>
      </Card>

      {/* Assign Contractor Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Contractor</DialogTitle>
            {selectedJob && (
              <p className="text-sm text-slate-500">{selectedJob.task_name}</p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Contractor *</Label>
              <Select value={assignData.contractor_id} onValueChange={(v) => setAssignData({...assignData, contractor_id: v})}>
                <SelectTrigger data-testid="assign-contractor-select">
                  <SelectValue placeholder="Select contractor" />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company || c.name} {c.specialties?.length > 0 && `(${c.specialties[0]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scheduled Date (Optional)</Label>
              <Input 
                type="date"
                value={assignData.scheduled_date}
                onChange={(e) => setAssignData({...assignData, scheduled_date: e.target.value})}
                data-testid="scheduled-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssign} className="bg-[#1F7A78] hover:bg-[#16615f]" data-testid="assign-btn">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Job</DialogTitle>
            {selectedJob && (
              <p className="text-sm text-slate-500">{selectedJob.task_name}</p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Completion Notes</Label>
              <Textarea 
                value={completeData.completion_notes}
                onChange={(e) => setCompleteData({...completeData, completion_notes: e.target.value})}
                placeholder="Enter completion notes..."
                rows={3}
                data-testid="completion-notes"
              />
            </div>
            <div>
              <Label>Actual Cost (£)</Label>
              <Input 
                type="number"
                value={completeData.actual_amount}
                onChange={(e) => setCompleteData({...completeData, actual_amount: e.target.value})}
                placeholder="Enter actual cost"
                data-testid="actual-amount"
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                checked={completeData.certificate_uploaded}
                onChange={(e) => setCompleteData({...completeData, certificate_uploaded: e.target.checked})}
                id="certificate"
                data-testid="certificate-checkbox"
              />
              <Label htmlFor="certificate">Certificate Uploaded</Label>
            </div>
            {selectedJob?.source_type === 'PPM' && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4 inline mr-2" />
                This will automatically update the PPM schedule and roll forward the next due date.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
            <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700" data-testid="complete-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
